import React, { useState } from 'react';
import ModuleLayout from '../../layouts/ModuleLayout';
import {
  Wallet,
  Users,
  Calculator,
  FileText,
  Calendar,
  Download,
  Upload,
  Play,
  CheckCircle2,
  Clock,
  AlertTriangle,
  IndianRupee,
  TrendingUp,
  Building2,
  Filter,
  Search,
  ChevronDown,
  MoreVertical,
  Eye,
  Send,
  Printer,
  Settings,
  Shield,
  FileSpreadsheet,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
} from 'lucide-react';

// Dummy data for payroll
const dummyEmployees = [
  { id: 1, name: 'Rahul Sharma', empCode: 'EMP001', department: 'Kitchen', designation: 'Head Chef', basicSalary: 45000, status: 'processed', netPay: 38500, pf: 5400, esi: 338, pt: 200, tds: 562 },
  { id: 2, name: 'Priya Patel', empCode: 'EMP002', department: 'Service', designation: 'Floor Manager', basicSalary: 35000, status: 'pending', netPay: 0, pf: 4200, esi: 263, pt: 200, tds: 0 },
  { id: 3, name: 'Amit Kumar', empCode: 'EMP003', department: 'Kitchen', designation: 'Sous Chef', basicSalary: 32000, status: 'processed', netPay: 27840, pf: 3840, esi: 240, pt: 200, tds: 0 },
  { id: 4, name: 'Sneha Reddy', empCode: 'EMP004', department: 'Service', designation: 'Senior Waiter', basicSalary: 22000, status: 'on-hold', netPay: 0, pf: 2640, esi: 165, pt: 150, tds: 0 },
  { id: 5, name: 'Vikram Singh', empCode: 'EMP005', department: 'Kitchen', designation: 'Line Cook', basicSalary: 20000, status: 'pending', netPay: 0, pf: 2400, esi: 150, pt: 150, tds: 0 },
  { id: 6, name: 'Anita Verma', empCode: 'EMP006', department: 'Admin', designation: 'Accountant', basicSalary: 38000, status: 'processed', netPay: 32680, pf: 4560, esi: 285, pt: 200, tds: 275 },
];

const payrollStats = {
  totalEmployees: 24,
  processed: 18,
  pending: 4,
  onHold: 2,
  totalGross: 584000,
  totalDeductions: 87600,
  totalNet: 496400,
  pfContribution: 70080,
  esiContribution: 4380,
};

const recentPayrolls = [
  { month: 'December 2024', status: 'current', processedOn: null, totalNet: 0 },
  { month: 'November 2024', status: 'completed', processedOn: '28 Nov 2024', totalNet: 492500 },
  { month: 'October 2024', status: 'completed', processedOn: '29 Oct 2024', totalNet: 485200 },
  { month: 'September 2024', status: 'completed', processedOn: '28 Sep 2024', totalNet: 478900 },
];

const complianceItems = [
  { id: 1, name: 'PF Challan - Dec 2024', dueDate: '15 Jan 2025', status: 'pending', amount: 70080 },
  { id: 2, name: 'ESI Challan - Dec 2024', dueDate: '15 Jan 2025', status: 'pending', amount: 4380 },
  { id: 3, name: 'Professional Tax - Q4', dueDate: '31 Jan 2025', status: 'pending', amount: 4500 },
  { id: 4, name: 'TDS Return - Q3', dueDate: '31 Jan 2025', status: 'completed', amount: 12450 },
];

const TABS = [
  { id: 'process', label: 'Salary Processing', icon: Calculator },
  { id: 'reports', label: 'Payroll Reports', icon: FileText },
  { id: 'compliance', label: 'Compliance', icon: Shield },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function PayrollManagementPage() {
  const [activeTab, setActiveTab] = useState('process');
  const [selectedMonth, setSelectedMonth] = useState('December 2024');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');

  const filteredEmployees = dummyEmployees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.empCode.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDept = filterDepartment === 'all' || emp.department === filterDepartment;
    return matchesSearch && matchesDept;
  });

  const getStatusBadge = (status) => {
    const styles = {
      processed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      'on-hold': 'bg-red-100 text-red-700 border-red-200',
      completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      current: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    const labels = {
      processed: 'Processed',
      pending: 'Pending',
      'on-hold': 'On Hold',
      completed: 'Completed',
      current: 'In Progress',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <ModuleLayout>
      <div className="min-h-full bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 pt-5 pb-0">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Payroll Management</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Process salaries, manage compliance & generate reports
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>December 2024</option>
                  <option>November 2024</option>
                  <option>October 2024</option>
                </select>
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm">
                  <Play size={16} />
                  Run Payroll
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 -mb-px">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
                      isActive
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="font-medium text-sm">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'process' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users size={20} className="text-blue-600" />
                    </div>
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                      {payrollStats.processed}/{payrollStats.totalEmployees}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{payrollStats.totalEmployees}</p>
                  <p className="text-sm text-gray-500">Total Employees</p>
                  <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(payrollStats.processed / payrollStats.totalEmployees) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <IndianRupee size={20} className="text-emerald-600" />
                    </div>
                    <ArrowUpRight size={16} className="text-emerald-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">₹{(payrollStats.totalGross / 1000).toFixed(0)}K</p>
                  <p className="text-sm text-gray-500">Gross Salary</p>
                  <p className="text-xs text-emerald-600 mt-1">+2.4% from last month</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <ArrowDownRight size={20} className="text-red-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">₹{(payrollStats.totalDeductions / 1000).toFixed(0)}K</p>
                  <p className="text-sm text-gray-500">Total Deductions</p>
                  <p className="text-xs text-gray-400 mt-1">PF + ESI + PT + TDS</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Wallet size={20} className="text-violet-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">₹{(payrollStats.totalNet / 1000).toFixed(0)}K</p>
                  <p className="text-sm text-gray-500">Net Payable</p>
                  <p className="text-xs text-blue-600 mt-1">{payrollStats.pending} pending</p>
                </div>
              </div>

              {/* Filters & Actions */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <select
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Departments</option>
                      <option value="Kitchen">Kitchen</option>
                      <option value="Service">Service</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition">
                      <Upload size={14} />
                      Import
                    </button>
                    <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition">
                      <Download size={14} />
                      Export
                    </button>
                  </div>
                </div>
              </div>

              {/* Employee Payroll Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Employee Payroll - {selectedMonth}</h3>
                  <div className="flex items-center gap-2">
                    <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">Select All</button>
                    <button className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition">
                      <CheckCircle2 size={14} />
                      Process Selected
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          <input type="checkbox" className="rounded border-gray-300" />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Department</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Basic</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">PF</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">ESI</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Net Pay</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredEmployees.map((emp) => (
                        <tr key={emp.id} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3">
                            <input type="checkbox" className="rounded border-gray-300" />
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900">{emp.name}</p>
                              <p className="text-xs text-gray-500">{emp.empCode} • {emp.designation}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{emp.department}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">₹{emp.basicSalary.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-right">₹{emp.pf.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-right">₹{emp.esi}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                            {emp.netPay > 0 ? `₹${emp.netPay.toLocaleString()}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-center">{getStatusBadge(emp.status)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition" title="View">
                                <Eye size={14} />
                              </button>
                              <button className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition" title="Send Payslip">
                                <Send size={14} />
                              </button>
                              <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition" title="Print">
                                <Printer size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="space-y-6">
              {/* Report Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { title: 'Payroll Register', desc: 'Monthly salary register with all components', icon: FileText, color: 'blue' },
                  { title: 'Bank Statement', desc: 'Salary transfer file for bank upload', icon: Building2, color: 'emerald' },
                  { title: 'PF Statement', desc: 'EPF contribution details for ECR', icon: Shield, color: 'violet' },
                  { title: 'ESI Statement', desc: 'ESI contribution summary', icon: Shield, color: 'amber' },
                  { title: 'TDS Report', desc: 'Tax deduction summary for Form 16', icon: IndianRupee, color: 'rose' },
                  { title: 'Cost Analysis', desc: 'Department-wise salary cost breakdown', icon: PieChart, color: 'cyan' },
                ].map((report, idx) => {
                  const Icon = report.icon;
                  const colorClasses = {
                    blue: 'bg-blue-100 text-blue-600 border-blue-200',
                    emerald: 'bg-emerald-100 text-emerald-600 border-emerald-200',
                    violet: 'bg-violet-100 text-violet-600 border-violet-200',
                    amber: 'bg-amber-100 text-amber-600 border-amber-200',
                    rose: 'bg-rose-100 text-rose-600 border-rose-200',
                    cyan: 'bg-cyan-100 text-cyan-600 border-cyan-200',
                  };
                  return (
                    <button
                      key={idx}
                      className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:shadow-md hover:border-blue-300 transition group"
                    >
                      <div className={`w-10 h-10 ${colorClasses[report.color]} rounded-lg flex items-center justify-center mb-3`}>
                        <Icon size={20} />
                      </div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition">{report.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{report.desc}</p>
                      <div className="mt-3 flex items-center gap-2">
                        <button className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                          <Eye size={12} /> View
                        </button>
                        <button className="text-xs text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1">
                          <Download size={12} /> Download
                        </button>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Recent Payrolls */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Payroll History</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {recentPayrolls.map((payroll, idx) => (
                    <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Calendar size={18} className="text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{payroll.month}</p>
                          <p className="text-xs text-gray-500">
                            {payroll.processedOn ? `Processed on ${payroll.processedOn}` : 'Not processed yet'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {payroll.totalNet > 0 && (
                          <p className="font-semibold text-gray-900">₹{payroll.totalNet.toLocaleString()}</p>
                        )}
                        {getStatusBadge(payroll.status)}
                        <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
                          <MoreVertical size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="space-y-6">
              {/* Compliance Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <Shield size={24} />
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full">EPF</span>
                  </div>
                  <p className="text-3xl font-bold">₹{(payrollStats.pfContribution / 1000).toFixed(1)}K</p>
                  <p className="text-sm text-blue-100 mt-1">PF Contribution (Employer + Employee)</p>
                  <p className="text-xs text-blue-200 mt-2">Due: 15th of next month</p>
                </div>

                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <Shield size={24} />
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full">ESI</span>
                  </div>
                  <p className="text-3xl font-bold">₹{(payrollStats.esiContribution / 1000).toFixed(1)}K</p>
                  <p className="text-sm text-emerald-100 mt-1">ESI Contribution (4% of Gross)</p>
                  <p className="text-xs text-emerald-200 mt-2">Due: 15th of next month</p>
                </div>

                <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-5 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <IndianRupee size={24} />
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full">TDS</span>
                  </div>
                  <p className="text-3xl font-bold">₹837</p>
                  <p className="text-sm text-violet-100 mt-1">TDS Deducted (This Month)</p>
                  <p className="text-xs text-violet-200 mt-2">Quarterly Return: 31st Jan</p>
                </div>
              </div>

              {/* Compliance Tasks */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Compliance Tasks</h3>
                  <button className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                    <RefreshCw size={14} />
                    Refresh Status
                  </button>
                </div>
                <div className="divide-y divide-gray-100">
                  {complianceItems.map((item) => (
                    <div key={item.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition">
                      <div className="flex items-center gap-3">
                        {item.status === 'completed' ? (
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                            <CheckCircle2 size={16} className="text-emerald-600" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <Clock size={16} className="text-amber-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-xs text-gray-500">Due: {item.dueDate}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="font-semibold text-gray-900">₹{item.amount.toLocaleString()}</p>
                        {getStatusBadge(item.status)}
                        {item.status !== 'completed' && (
                          <button className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition">
                            Pay Now
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Salary Components */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">Salary Components</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Configure earnings and deduction components</p>
                  </div>
                  <div className="p-4 space-y-3">
                    {[
                      { name: 'Basic Salary', type: 'earning', percent: '50% of CTC' },
                      { name: 'HRA', type: 'earning', percent: '20% of Basic' },
                      { name: 'Transport Allowance', type: 'earning', fixed: '₹1,600/month' },
                      { name: 'PF (Employee)', type: 'deduction', percent: '12% of Basic' },
                      { name: 'ESI (Employee)', type: 'deduction', percent: '0.75% of Gross' },
                      { name: 'Professional Tax', type: 'deduction', fixed: 'As per slab' },
                    ].map((comp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${comp.type === 'earning' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          <span className="font-medium text-gray-900">{comp.name}</span>
                        </div>
                        <span className="text-sm text-gray-600">{comp.percent || comp.fixed}</span>
                      </div>
                    ))}
                    <button className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition">
                      + Add Component
                    </button>
                  </div>
                </div>

                {/* Statutory Settings */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">Statutory Settings</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Configure PF, ESI, TDS settings</p>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">PF Registration</p>
                        <p className="text-xs text-gray-500">EPFO Establishment Code</p>
                      </div>
                      <span className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200">DLCPM0012345</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">ESI Registration</p>
                        <p className="text-xs text-gray-500">ESIC Code</p>
                      </div>
                      <span className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200">11001234567890</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">TAN Number</p>
                        <p className="text-xs text-gray-500">For TDS Deduction</p>
                      </div>
                      <span className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200">DELR12345E</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">PF Wage Ceiling</p>
                        <p className="text-xs text-gray-500">Maximum wage for PF calculation</p>
                      </div>
                      <span className="text-sm font-medium text-gray-900">₹15,000</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModuleLayout>
  );
}
